import {
  Alert,
  Button,
  Checkbox,
  DialogActions,
  DialogContent,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import { Box, Stack } from '@mui/system';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { editTaskTimeAction } from 'src/redux/actions/editTaskTimeAction';
import { therapistAssignUndoAction } from 'src/redux/actions/therapistAssignmentUndoAction';
import {
  therapistAssignTaskAction,
  therapistAssignTaskResetAction,
} from 'src/redux/actions/therapistAssignTaskAction';

type Props = {
  assignGroupData: any;
  setOpenGroupDialog: any;
  providerId: string;
  selectedDate: any;
  therapistIdToDrop: string;
  meta: any;
  setTaskSelectedId: any;
  setCloseGroupDialog: any;
  isV2?: boolean;
  assignTaskPage?: any;
  currentTab: string;
};

function GroupTherapistTask({
  setOpenGroupDialog,
  assignGroupData,
  providerId,
  selectedDate,
  therapistIdToDrop,
  meta,
  isV2,
  setCloseGroupDialog,
  assignTaskPage,
  currentTab,
}: Props) {
  const dispatch = useDispatch();
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectedNames, setSelectedNames] = useState([]);
  const [openSnackBar, setOpenSnackBar] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [closeDialog, setCloseDialog] = useState(false);

  const [durationMode, setDurationMode] = useState(false);
  const [numberOfMins, setNumberOfMins] = useState<number>(0);

  const assignGroupDataFromRedux = useSelector(
    (state: any) => state?.therapistAssignTaskReducer?.response?.data
  );

  const selectedTasksFromRedux = useSelector(
    (state: any) => state?.therapistAssignTaskReducer?.selectedTasks
  );

  useEffect(() => {
    if (!!assignGroupDataFromRedux?.concurrentAndGroupSession?.length) {
      setDurationMode(true);
      return;
    }
    if (!assignGroupDataFromRedux || !assignGroupDataFromRedux?.label) {
      dispatch(therapistAssignTaskResetAction());
      setAlertMessage('All task assigned successfully');
      setOpenSnackBar(true);
      setSelectedItems([]);
      setSelectedNames([]);
      setOpenGroupDialog(false);
      setCloseGroupDialog(true);
    } else {
      if (closeDialog) {
        setAlertMessage('Task assigned successfully, Please select another patient to group with');
        setOpenSnackBar(true);
        setSelectedItems([]);
        setSelectedNames([]);
      }
    }
  }, [assignGroupDataFromRedux]);

  const handleYes = () => {
    if (durationMode) {
      dispatch(
        editTaskTimeAction({
          providerId: providerId,
          time: numberOfMins,
          requestBody: {
            taskUuids: [],
            groupUuid: assignGroupDataFromRedux?.concurrentAndGroupSession[0]?.groupUuid,
          },
          isAssigned: true,
        })
      );

      if (
        assignGroupDataFromRedux.processedData.length ||
        assignGroupDataFromRedux.unprocessedData.length
      ) {
        setSelectedItems([]);
        setSelectedNames([]);
        setDurationMode(false);
      } else {
        dispatch(therapistAssignTaskResetAction());
        setOpenGroupDialog(false);
        setCloseGroupDialog(true);
      }
      return;
    }

    dispatch(
      therapistAssignTaskAction({
        providerId: providerId,
        therapistId: therapistIdToDrop,
        date: selectedDate,
        currentDate: selectedDate ? moment(selectedDate, 'DD-MM-YYYY').format('YYYY-MM-DD') : '',
        formateDate: moment(selectedDate, 'DD-MM-YYYY').format('YYYY-MM-DD'),
        refresh: false,
        meta: meta,
        filterByUuid: meta?.filterByUuid,
        destinationAssignPage: assignTaskPage,
        isV2,
        data:
          assignGroupDataFromRedux?.groups && assignGroupDataFromRedux?.groups?.length !== 0
            ? {
                wantToGroup: true,
                unprocessedData: assignGroupData.unprocessedData.map((item: any) => item.uuid),
                processedData: assignGroupData.processedData.map((item: any) => item.uuid),
                groupUuid: handleDataSelect(selectedItems)[0],
              }
            : {
                wantToGroup: true,
                unprocessedData: [
                  // eslint-disable-next-line no-unsafe-optional-chaining
                  ...assignGroupData?.unprocessedData.map((item: any) => item.uuid),
                  ...handleUnmatchedData(selectedItems),
                ],
                processedData: handleProcessedData(selectedItems),
                groupUuid: null,
              },
      })
    );
    setCloseDialog(true);
  };

  const handleDataSelect = (items: any) => {
    if (assignGroupDataFromRedux?.groups && assignGroupDataFromRedux?.groups.length !== 0) {
      items = Array.isArray(items) ? items : [items];
    }
    const matchingUUIDs: any = [];

    items?.forEach((name: any) => {
      let match;
      if (assignGroupDataFromRedux?.groups && assignGroupDataFromRedux?.groups.length !== 0) {
        match = assignGroupData.groups.find((item: any) => item.uuid === name);
      } else {
        match = assignGroupData.processedData.find((item: any) => item.uuid === name);
      }
      if (match) {
        matchingUUIDs.push(match.uuid);
      }
    });
    return matchingUUIDs;
  };

  const handleUnmatchedData = (items: any) => {
    const unmatchedUuids = assignGroupData?.processedData
    ?.filter((item: any) => !items.includes(item.uuid))
    .map((item: any) => item.uuid);
    return unmatchedUuids;
  };

  const handleProcessedData = (items: any) => {
    const matchingUUIDs: any = [];
    items.forEach((name: any) => {
      const match = assignGroupData.processedData.find((item: any) => item.uuid === name);
      if (match) {
        matchingUUIDs.push(match.uuid);
      }
    });
    return matchingUUIDs;
  };

  const handleNo = () => {
    if (durationMode) {
      const matchingTaskIds = assignGroupDataFromRedux.concurrentAndGroupSession
        .filter((task:any) => selectedTasksFromRedux.includes(task.taskUuid))
        .map((task:any) => task.taskUuid);

      dispatch(
        therapistAssignUndoAction({
          providerId,
          therapistId: therapistIdToDrop,
          type: '',
          active: true,
          date: selectedDate,
          currentDate: selectedDate ? moment(selectedDate, 'DD-MM-YYYY').format('YYYY-MM-DD') : '',
          formateDate: moment(selectedDate, 'DD-MM-YYYY').format('YYYY-MM-DD'),
          refresh: false,
          meta: meta,
          filterByUuid: meta?.filterByUuid,
          destinationAssignPage: assignTaskPage,
          discipline: currentTab,
          skill: meta.skill,
          sortBy: meta.skill,
          requestBody: {
            taskUuids: matchingTaskIds,
            groupUuid: null,
          },
          isV2: true,
        })
      );

      if (assignGroupDataFromRedux.processedData.length) {
        setSelectedItems([]);
        setSelectedNames([]);
        setDurationMode(false);
      } else {
        dispatch(therapistAssignTaskResetAction());
        setOpenGroupDialog(false);
        setCloseGroupDialog(true);
      }
      return;
    }
    dispatch(
      therapistAssignTaskAction({
        providerId: providerId,
        therapistId: therapistIdToDrop,
        currentDate: selectedDate ? moment(selectedDate, 'DD-MM-YYYY').format('YYYY-MM-DD') : '',
        formateDate: moment(selectedDate, 'DD-MM-YYYY').format('YYYY-MM-DD'),
        filterByUuid: meta?.filterByUuid,
        date: selectedDate,
        refresh: false,
        meta: meta,
        destinationAssignPage: assignTaskPage,
        isV2,
        data: {
          wantToGroup: false,
          unprocessedData: assignGroupData?.unprocessedData.map((item: any) => item.uuid),
          processedData: assignGroupData?.processedData.map((item: any) => item.uuid),
          groupUuid: null,
        },
      })
    );
    setCloseDialog(true);
  };

  const handleChange = (event: any) => {
    const uuids = Array.isArray(event.target.value) ? event.target.value : [event.target.value];
    const originaldata =
      (!assignGroupDataFromRedux?.groups && assignGroupDataFromRedux?.label) ||
      (!assignGroupDataFromRedux?.groups && assignGroupDataFromRedux?.groups?.length === 0)
        ? assignGroupData?.processedData
        : assignGroupData?.groups;

    const result = uuids?.map((targetUUID: string) => {
      const matchingObject = originaldata.find((item: any) => {
        return item.uuid === targetUUID;
      });

      return matchingObject ? matchingObject.patientNames[0] : null;
    });

    const newValue = Array.isArray(event.target.value) ? event.target.value : [event.target.value];

    if (
      (!assignGroupDataFromRedux?.groups && assignGroupDataFromRedux?.label) ||
      (!assignGroupDataFromRedux?.groups && assignGroupDataFromRedux?.groups?.length === 0)
    ) {
      if (assignGroupDataFromRedux?.type === 'GROUP' && newValue.length <= 6) {
        setSelectedItems(newValue);
        setSelectedNames(result);
      }

      if (assignGroupDataFromRedux?.type === 'CONCURRENT' && newValue.length <= 2) {
        setSelectedItems(newValue);
        setSelectedNames(result);
      }
    } else {
      setSelectedItems(newValue);
      setSelectedNames(result);
    }
  };

  const renderProcessedDataOptions = () => {
    return assignGroupData?.processedData?.map((option: any) => (
      <MenuItem
        key={option.uuid}
        value={option.uuid}
      >
        <Checkbox checked={selectedItems?.some((item: any) => item === option.uuid)} />
        <ListItemText primary={option?.patientNames[0]} />
      </MenuItem>
    ));
  };

  const renderGroupDataOptions = () => {
    return assignGroupData?.groups?.map((option: any) => (
      <MenuItem
        key={option.uuid}
        value={option.uuid}
      >
        <Typography>
          {option.patientNames.join(', ').length > 67
            ? option.patientNames.join(', ').substring(0, 67) + '...'
            : option.patientNames.join(', ')}
        </Typography>
      </MenuItem>
    ));
  };

  return (
    <Box width={600}>
      <DialogContent>
        {durationMode ? (
          <>
            <Typography
              mb={3}
              variant="h6"
            >
              Mismatched Durations
            </Typography>

            <Stack
              paddingLeft={'20px'}
              mb={3}
            >
              {assignGroupDataFromRedux?.concurrentAndGroupSession?.map(
                (item: any, index: number) => {
                  return (
                    <Box
                      display={'flex'}
                      columnGap={2}
                      key={index}
                    >
                      <Typography variant="body1">{item.patientName}</Typography>
                      <Typography variant="subtitle1">{item.taskMin} minutes</Typography>
                    </Box>
                  );
                }
              )}
            </Stack>

            <Typography
              variant="body2"
              mb={1}
            >
              Set a session duration for the group. Their plans will update accordingly.
            </Typography>

            <TextField
              fullWidth
              label="Session duration"
              placeholder="Session duration"
              name="numberOfMinutes"
              value={numberOfMins}
              type="number"
              onChange={(e) => {
                setNumberOfMins(parseInt(e.target.value));
              }}
            />
          </>
        ) : (
          <>
            <Typography
              mb={3}
              variant="h6"
            >
              {assignGroupDataFromRedux?.type === 'GROUP'
                ? 'Group patients with common skills ?'
                : assignGroupDataFromRedux?.type === 'CONCURRENT'
                ? 'Group patients for a concurrent session ?'
                : ''}
            </Typography>
            <FormControl fullWidth>
              <InputLabel
                sx={{
                  background: 'white !important',
                  padding: '0px 5px !important',
                }}
              >
                {assignGroupDataFromRedux?.type === 'GROUP'
                  ? 'Select patients for group session'
                  : assignGroupDataFromRedux?.type === 'CONCURRENT'
                  ? 'Select patients to group with'
                  : ''}
              </InputLabel>
              {(!assignGroupDataFromRedux?.groups && assignGroupDataFromRedux?.label) ||
              (!assignGroupDataFromRedux?.groups &&
                assignGroupDataFromRedux?.groups?.length === 0) ? (
                <Select
                  multiple
                  value={selectedItems}
                  onChange={handleChange}
                  renderValue={(selected) => {
                    return selectedNames.join(', ');
                  }}
                >
                  {renderProcessedDataOptions()}
                </Select>
              ) : (
                <Select
                  value={selectedItems}
                  onChange={handleChange}
                >
                  {renderGroupDataOptions()}
                </Select>
              )}
            </FormControl>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          autoFocus
          onClick={handleNo}
        >
          {durationMode ? 'Cancel' : 'No'}
        </Button>
        <Button
          autoFocus
          variant="contained"
          onClick={handleYes}
          disabled={!durationMode ? selectedItems.length === 0 : !numberOfMins}
        >
          {durationMode ? 'Confirm Duration' : 'Yes'}
        </Button>
      </DialogActions>
      <Snackbar
        open={openSnackBar}
        autoHideDuration={5000}
        onClose={() => setOpenSnackBar(false)}
      >
        <Alert
          onClose={() => setOpenSnackBar(false)}
          severity="success"
          sx={{ width: '100%' }}
        >
          {alertMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default GroupTherapistTask;
